/**
 * Profile Label Utilities
 *
 * Maps raw profile names from the API to i18n translation keys so that
 * profile names are displayed in the user's selected language throughout
 * the application (table columns, edit modal radio items, etc.).
 *
 * Uses `normalizeRole()` for diacritics-insensitive, case-insensitive
 * matching so that both `"Gestor"` and `"GESTOR"` resolve correctly.
 *
 * @module features/users/utils/profileLabels
 */
import { normalizeRole } from '@/shared/utils/normalizeRole';

/**
 * Map from normalized role key → i18n translation key.
 *
 * The i18n key uses English labels following standard i18n conventions.
 * Translations for each locale are defined in `en.json` and `pt.json`.
 *
 * The API sends `client_profile.name`, which is the UPPER_SNAKE key
 * (`SUPER_ADMIN`), not the human label — a profile missing from this map falls
 * through to the raw key and is rendered as-is on screen. Every profile seeded
 * by the API must therefore have an entry here.
 *
 * Note: "Super Admin" is rendered in caps ("SUPER ADMIN") in both locales, by
 * product decision; the entry lives in the locale files, not here.
 */
const NORMALIZED_TO_I18N_KEY: Record<string, string> = {
  ADMIN_AVANCADO: 'Advanced Admin',
  ADVANCED_ADMIN: 'Advanced Admin',
  ADMIN_BASICO: 'Basic Admin',
  BASIC_ADMIN: 'Basic Admin',
  GESTOR: 'Manager',
  MANAGER: 'Manager',
  SUPERVISOR: 'Supervisor',
  OPERADOR: 'Operator',
  OPERATOR: 'Operator',
  COMERCIAL_BASICO: 'Basic Commercial',
  COMERCIAL: 'Basic Commercial',
  ADMINISTRATIVO: 'Administrative',
  SUPER_ADMIN: 'Super Admin',
  SUPERADMIN: 'Super Admin',
  ADVANCED_VIEWER: 'Advanced Viewer',
  VIEWER_AVANCADO: 'Advanced Viewer',
  BASIC_VIEWER: 'Basic Viewer',
  VIEWER_BASICO: 'Basic Viewer',
  CONTESTATION: 'Contestation',
  CONTESTACAO: 'Contestation',
};

/**
 * Returns the i18n key for a raw profile name.
 *
 * @param profileName - Raw profile name from the API (e.g. `"Gestor"`, `"Admin Básico"`)
 * @returns The i18n key string to pass to `t()`. Falls back to the original name
 *          if no mapping is found, so `t()` can still attempt a direct match.
 *
 * @example
 * ```ts
 * const key = getProfileI18nKey('Gestor');  // returns 'Manager'
 * const label = t(key);
 * // en → "Manager"
 * // pt → "Gestor"
 * ```
 */
export const getProfileI18nKey = (profileName: string): string => {
  const normalized = normalizeRole(profileName);
  return NORMALIZED_TO_I18N_KEY[normalized] ?? profileName;
};
